import { useMemo, useState, useEffect } from 'react'
import { Card, Row, Col, DatePicker, Button, Space, Table, Tag, message } from 'antd'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  ComposedChart,
} from 'recharts'
import type { Dayjs } from 'dayjs'
import { analyticsApi } from '../../services/api'
import Permission from '../../components/Permission'

const { RangePicker } = DatePicker

const Analytics = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [appliedRange, setAppliedRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [reportGenerating, setReportGenerating] = useState(false)
  const [indicatorsFromApi, setIndicatorsFromApi] = useState<
    Array<{ key: string; metric: string; description?: string; frequency?: string; unit?: string; trend?: { time: string; value: number }[] }> | null
  >(null)
  const [assetDistributionData, setAssetDistributionData] = useState<any>(null)
  const [trendAnalysisData, setTrendAnalysisData] = useState<any>(null)
  const [comparisonAnalysisData, setComparisonAnalysisData] = useState<any>(null)
  const [amountDimensionData, setAmountDimensionData] = useState<any>(null)
  const [reportsListData, setReportsListData] = useState<any>(null)

  const dateRangeBody = useMemo(() => {
    const range = Array.isArray(appliedRange) && appliedRange.length >= 2 ? appliedRange : null
    return range
      ? { dateRange: { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') } }
      : {}
  }, [appliedRange])

  useEffect(() => {
    setAnalyticsLoading(true)
    const body = dateRangeBody
    Promise.all([
      analyticsApi.indicators(body),
      analyticsApi.assetDistribution(body),
      analyticsApi.trendAnalysis(body),
      analyticsApi.comparisonAnalysis(body),
      analyticsApi.amountDimension(body),
      analyticsApi.reportsList(body),
    ])
      .then(([indRes, distRes, trendRes, compareRes, amountRes, reportsRes]) => {
        const list = indRes?.data?.indicators
        setIndicatorsFromApi(Array.isArray(list) ? list : null)
        setAssetDistributionData(distRes?.data ?? null)
        setTrendAnalysisData(trendRes?.data ?? null)
        setComparisonAnalysisData(compareRes?.data ?? null)
        setAmountDimensionData(amountRes?.data ?? null)
        setReportsListData(reportsRes?.data ?? null)
      })
      .catch(() => {
        setIndicatorsFromApi(null)
        setAssetDistributionData(null)
        setTrendAnalysisData(null)
        setComparisonAnalysisData(null)
        setAmountDimensionData(null)
        setReportsListData(null)
      })
      .finally(() => setAnalyticsLoading(false))
  }, [dateRangeBody])

  const operationIndicators = [
    {
      key: 'totalAssets',
      metric: '资产总数',
      description: '系统中的资产总数',
      frequency: '实时',
    },
    {
      key: 'totalValue',
      metric: '资产总价值',
      description: '所有资产的总金额',
      frequency: '实时',
    },
    {
      key: 'typeDistribution',
      metric: '按类型分布',
      description: '债权、权益、通证的数量和金额',
      frequency: '实时',
    },
    {
      key: 'statusDistribution',
      metric: '按状态分布',
      description: '活跃、过期、待审批等各状态的资产数量',
      frequency: '实时',
    },
    {
      key: 'avgQuality',
      metric: '平均质量评分',
      description: '所有资产的平均质量评分',
      frequency: '1小时',
    },
    {
      key: 'problemAssets',
      metric: '有问题的资产数',
      description: '质量评分低于阈值的资产数量',
      frequency: '1小时',
    },
  ]

  // 资产分布分析数据
  const assetTypeData = [
    { type: '债权', value: 420, amount: 5200 },
    { type: '权益', value: 260, amount: 3800 },
    { type: '通证', value: 120, amount: 2100 },
  ]

  const assetStatusData = [
    { status: '活跃', count: 520 },
    { status: '过期', count: 80 },
    { status: '待审批', count: 60 },
    { status: '暂停', count: 40 },
  ]

  const assetSourceData = [
    { source: '核心系统', count: 380, amount: 4800 },
    { source: '外部接入', count: 220, amount: 3100 },
    { source: '历史导入', count: 100, amount: 1200 },
    { source: '其他', count: 20, amount: 300 },
  ]

  const regionData = [
    { region: '华东', count: 260, amount: 3200 },
    { region: '华北', count: 180, amount: 2400 },
    { region: '华南', count: 160, amount: 2100 },
    { region: '西部', count: 80, amount: 900 },
  ]

  // 趋势分析数据
  const growthTrendData = [
    { date: '01-01', assets: 600, amount: 8200 },
    { date: '01-08', assets: 620, amount: 8400 },
    { date: '01-15', assets: 640, amount: 8600 },
    { date: '01-22', assets: 665, amount: 8900 },
    { date: '01-29', assets: 680, amount: 9100 },
  ]

  const amountTrendData = [
    { date: '01-01', total: 8200, avg: 13.7, max: 220 },
    { date: '01-08', total: 8400, avg: 13.9, max: 225 },
    { date: '01-15', total: 8600, avg: 14.1, max: 230 },
    { date: '01-22', total: 8900, avg: 14.4, max: 238 },
    { date: '01-29', total: 9100, avg: 14.6, max: 242 },
  ]

  const maturityTrendData = [
    { date: '近7天', count: 36, amount: 820 },
    { date: '近30天', count: 110, amount: 2400 },
    { date: '近90天', count: 260, amount: 5400 },
    { date: '90天以上', count: 140, amount: 3100 },
  ]

  // 对比分析数据
  const periodCompareData = [
    { period: '本月', assets: 680, amount: 9100 },
    { period: '上月', assets: 640, amount: 8600 },
    { period: '去年同期', assets: 560, amount: 7600 },
  ]

  const categoryCompareData = [
    { category: '债权', indicator: 82 },
    { category: '权益', indicator: 76 },
    { category: '通证', indicator: 68 },
    { category: '其他', indicator: 60 },
  ]

  const ownerCompareData = [
    { owner: '张三', assets: 180, amount: 2600 },
    { owner: '李四', assets: 160, amount: 2400 },
    { owner: '王五', assets: 140, amount: 2200 },
    { owner: '赵六', assets: 120, amount: 1900 },
  ]

  // 金额维度分析数据
  const amountDistributionData = [
    { range: '0-50万', count: 180 },
    { range: '50-100万', count: 140 },
    { range: '100-300万', count: 120 },
    { range: '300-500万', count: 60 },
    { range: '500万以上', count: 40 },
  ]

  const topAssets = [
    { rank: 1, id: 'AST-20240101', name: '核心企业应收账款池A', type: '债权', amount: 520 },
    { rank: 2, id: 'AST-20240102', name: '资产证券化底层资产B', type: '通证', amount: 430 },
    { rank: 3, id: 'AST-20240103', name: '供应链保理资产C', type: '债权', amount: 390 },
    { rank: 4, id: 'AST-20240104', name: '股权质押资产D', type: '权益', amount: 360 },
    { rank: 5, id: 'AST-20240105', name: '跨境应收资产E', type: '债权', amount: 340 },
    { rank: 6, id: 'AST-20240106', name: '票据资产组合F', type: '债权', amount: 320 },
    { rank: 7, id: 'AST-20240107', name: '通证化资产G', type: '通证', amount: 300 },
    { rank: 8, id: 'AST-20240108', name: '长期股权投资H', type: '权益', amount: 280 },
    { rank: 9, id: 'AST-20240109', name: '短期融资资产I', type: '债权', amount: 260 },
    { rank: 10, id: 'AST-20240110', name: 'ABS底层资产J', type: '债权', amount: 240 },
  ]

  const paretoData = [
    { bucket: 'Top 10%', amount: 4800, cumulativeRatio: 52 },
    { bucket: 'Top 20%', amount: 6700, cumulativeRatio: 73 },
    { bucket: 'Top 30%', amount: 7800, cumulativeRatio: 85 },
    { bucket: 'Top 40%', amount: 8400, cumulativeRatio: 92 },
    { bucket: 'Top 50%', amount: 8900, cumulativeRatio: 97 },
  ]

  const pieColors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444']

  const assetTypeDataFinal = useMemo(
    () => (Array.isArray(assetDistributionData?.assetTypeData) ? assetDistributionData.assetTypeData : assetTypeData),
    [assetDistributionData]
  )
  const assetStatusDataFinal = useMemo(
    () => (Array.isArray(assetDistributionData?.assetStatusData) ? assetDistributionData.assetStatusData : assetStatusData),
    [assetDistributionData]
  )
  const assetSourceDataFinal = useMemo(
    () => (Array.isArray(assetDistributionData?.assetSourceData) ? assetDistributionData.assetSourceData : assetSourceData),
    [assetDistributionData]
  )
  const regionDataFinal = useMemo(
    () => (Array.isArray(assetDistributionData?.regionData) ? assetDistributionData.regionData : regionData),
    [assetDistributionData]
  )

  const growthTrendDataFinal = useMemo(
    () => (Array.isArray(trendAnalysisData?.growthTrendData) ? trendAnalysisData.growthTrendData : growthTrendData),
    [trendAnalysisData]
  )
  const amountTrendDataFinal = useMemo(
    () => (Array.isArray(trendAnalysisData?.amountTrendData) ? trendAnalysisData.amountTrendData : amountTrendData),
    [trendAnalysisData]
  )
  const maturityTrendDataFinal = useMemo(
    () => (Array.isArray(trendAnalysisData?.maturityTrendData) ? trendAnalysisData.maturityTrendData : maturityTrendData),
    [trendAnalysisData]
  )

  const periodCompareDataFinal = useMemo(
    () => (Array.isArray(comparisonAnalysisData?.periodCompareData) ? comparisonAnalysisData.periodCompareData : periodCompareData),
    [comparisonAnalysisData]
  )
  const categoryCompareDataFinal = useMemo(
    () => (Array.isArray(comparisonAnalysisData?.categoryCompareData) ? comparisonAnalysisData.categoryCompareData : categoryCompareData),
    [comparisonAnalysisData]
  )
  const ownerCompareDataFinal = useMemo(
    () => (Array.isArray(comparisonAnalysisData?.ownerCompareData) ? comparisonAnalysisData.ownerCompareData : ownerCompareData),
    [comparisonAnalysisData]
  )

  const amountDistributionDataFinal = useMemo(
    () => (Array.isArray(amountDimensionData?.amountDistributionData) ? amountDimensionData.amountDistributionData : amountDistributionData),
    [amountDimensionData]
  )
  const topAssetsFinal = useMemo(
    () => (Array.isArray(amountDimensionData?.topAssets) ? amountDimensionData.topAssets : topAssets),
    [amountDimensionData]
  )
  const paretoDataFinal = useMemo(
    () => (Array.isArray(amountDimensionData?.paretoData) ? amountDimensionData.paretoData : paretoData),
    [amountDimensionData]
  )

  const indicatorTemplates: Record<
    string,
    {
      unit?: string
      values: number[]
    }
  > = {
    totalAssets: {
      unit: '个',
      values: [560, 580, 600, 640, 680],
    },
    totalValue: {
      unit: '万',
      values: [7600, 7900, 8200, 8600, 9100],
    },
    typeDistribution: {
      unit: '种',
      values: [3, 3, 4, 4, 4],
    },
    statusDistribution: {
      unit: '种',
      values: [3, 4, 4, 4, 4],
    },
    avgQuality: {
      unit: '分',
      values: [78, 80, 82, 83, 84],
    },
    problemAssets: {
      unit: '个',
      values: [52, 48, 44, 40, 36],
    },
  }

  const indicatorTrends = useMemo(() => {
    const list = Array.isArray(indicatorsFromApi) ? indicatorsFromApi : null
    if (list?.length) {
      const result: Record<string, { unit?: string; data: { time: string; value: number }[] }> = {}
      list.forEach((ind) => {
        result[ind.key] = {
          unit: ind.unit,
          data: Array.isArray(ind.trend) ? ind.trend : [],
        }
      })
      return result
    }

    const buildTimes = (length: number): string[] => {
      const range = Array.isArray(appliedRange) && appliedRange.length >= 2 ? appliedRange : null
      if (!range) {
        const labels = ['T-4', 'T-3', 'T-2', 'T-1', 'T']
        return labels.slice(labels.length - length)
      }
      const [start, end] = range
      const totalDays = Math.max(end.startOf('day').diff(start.startOf('day'), 'day'), 1)
      const steps = length === 1 ? [0] : Array.from({ length }, (_, i) =>
        Math.round((totalDays * i) / (length - 1))
      )
      return steps.map(offset =>
        start.startOf('day').add(offset, 'day').format('MM-DD')
      )
    }

    const result: Record<string, { unit?: string; data: { time: string; value: number }[] }> = {}
    Object.entries(indicatorTemplates).forEach(([key, tpl]) => {
      const times = buildTimes(tpl.values.length)
      result[key] = {
        unit: tpl.unit,
        data: times.map((time, idx) => ({ time, value: tpl.values[idx] })),
      }
    })
    return result
  }, [appliedRange, indicatorsFromApi])

  return (
    <div className="space-y-6">
      {/* 页面标题和操作 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">分析报告</h1>
          <p className="text-gray-600">从多维度分析数字资产运行情况</p>
        </div>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(values) => setDateRange(values as [Dayjs, Dayjs] | null)}
            allowClear
          />
          <Button
            type="primary"
            disabled={!dateRange}
            loading={analyticsLoading}
            onClick={() => {
              if (dateRange) setAppliedRange(dateRange)
            }}
          >
            生成分析
          </Button>
          {reportsListData != null && (
            <span className="text-gray-500 text-sm">
              共 {Array.isArray(reportsListData?.items) ? reportsListData.items.length : (reportsListData?.total ?? 0)} 份报告
            </span>
          )}
          <Permission code="analytics:reports_create">
            <Button
              loading={reportGenerating}
              onClick={() => {
                setReportGenerating(true)
                analyticsApi.reportsCreate(
                  Array.isArray(appliedRange) && appliedRange.length >= 2
                    ? { dateRange: { from: appliedRange[0].format('YYYY-MM-DD'), to: appliedRange[1].format('YYYY-MM-DD') } }
                    : {}
                )
                  .then(() => message.success('报告生成成功'))
                  .catch(() => message.info('报告生成请求已发送，请稍后在报告中查看'))
                  .finally(() => setReportGenerating(false))
              }}
            >
              生成报告
            </Button>
          </Permission>
        </Space>
      </div>

      {/* 资产产品运行指标 */}
      <Card title="资产产品运行指标">
        <Row gutter={[16, 16]} className="mb-6">
          {(Array.isArray(indicatorsFromApi) && indicatorsFromApi.length ? indicatorsFromApi : operationIndicators).map((item, index) => {
            const trend = indicatorTrends[item.key]
            const latestValue = trend?.data[trend.data.length - 1]?.value

            return (
              <Col key={item.key} xs={24} md={12} lg={8}>
                <div className="h-56 flex flex-col">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-gray-800">{item.metric}</div>
                    <Tag color="blue" className="text-xs px-2 py-0">
                      {item.frequency}
                    </Tag>
          </div>
                  {latestValue !== undefined && (
                    <div className="text-2xl font-semibold text-gray-900 mb-2">
                      {latestValue}
                      {trend?.unit && (
                        <span className="text-sm text-gray-500 ml-1">{trend.unit}</span>
                      )}
          </div>
                  )}
          <div className="flex-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={trend?.data || []}
                        margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="time" hide />
                        <YAxis hide />
                        <Tooltip
                          formatter={(value: number) =>
                            trend?.unit ? `${value} ${trend.unit}` : value
                          }
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke={pieColors[index % pieColors.length]}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
          </div>
        </div>
              </Col>
            )
          })}
        </Row>
      </Card>

      {/* 资产产品分布分析 */}
      <Card title="资产产品分布分析">
      <Row gutter={[16, 28]}>
          <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">按类型分布（数量 & 金额占比）</div>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={assetTypeDataFinal}
                    dataKey="amount"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {assetTypeDataFinal.map((entry: { type: string }, index: number) => (
                      <Cell key={entry.type} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} 万`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
        </Col>
          <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">按状态分布（资产数量）</div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={assetStatusDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="数量" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
        </Col>
      </Row>

        <Row gutter={[16, 28]} className="mt-8 mb-6">
          <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">按来源分布（数量 & 金额）</div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={assetSourceDataFinal}
                  layout="vertical"
                  margin={{ top: 16, right: 16, bottom: 0, left: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="source" type="category" />
                  <Tooltip formatter={(value, name) => (name === '金额' ? `${value} 万` : value)} />
                  <Legend />
                  <Bar dataKey="count" name="数量" fill="#10b981" />
                  <Bar dataKey="amount" name="金额(万)" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
                </div>
              </Col>
              <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">地理分布（地区 & 金额）</div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={regionDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="region" />
                  <YAxis />
                  <Tooltip formatter={(value) => `${value} 万`} />
                  <Bar dataKey="amount" name="金额(万)" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
                </div>
              </Col>
            </Row>
      </Card>

      {/* 趋势分析 */}
      <Card title="趋势分析">
            <Row gutter={[16, 28]}>
              <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">资产增长趋势（数量 & 总金额）</div>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={growthTrendDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip formatter={(value, name) => (name === '总金额(万)' ? `${value} 万` : value)} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="assets" name="资产数量" fill="#3b82f6" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="amount"
                    name="总金额(万)"
                    stroke="#10b981"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
                </div>
              </Col>
              <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">金额趋势（总金额 / 平均金额 / 最大金额）</div>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={amountTrendDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" name="总金额(万)" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="avg" name="平均金额(万)" stroke="#10b981" />
                  <Line type="monotone" dataKey="max" name="最大金额(万)" stroke="#f59e0b" />
                </LineChart>
              </ResponsiveContainer>
                </div>
              </Col>
            </Row>

        <Row gutter={[16, 28]} className="mt-8 mb-6">
          <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">到期趋势（未来一段时间内到期资产）</div>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={maturityTrendDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip formatter={(value, name) => (name === '金额' ? `${value} 万` : value)} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="count" name="到期数量" fill="#8b5cf6" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="amount"
                    name="到期金额(万)"
                    stroke="#ef4444"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>
                </Card>
                
      {/* 对比分析 */}
      <Card title="对比分析">
        <Row gutter={[16, 28]} className="mb-6">
          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">周期对比（本月 / 上月 / 去年同期）</div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={periodCompareDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => (name === '金额' ? `${value} 万` : value)} />
                  <Legend />
                  <Bar dataKey="assets" name="资产数量" fill="#3b82f6" />
                  <Bar dataKey="amount" name="金额(万)" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
                            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">分类对比（资产指标评分）</div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryCompareDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="indicator" name="指标评分" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
              </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">责任人对比（管理资产数量 & 金额）</div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ownerCompareDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="owner" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => (name === '金额' ? `${value} 万` : value)} />
                  <Legend />
                  <Bar dataKey="assets" name="资产数量" fill="#8b5cf6" />
                  <Bar dataKey="amount" name="金额(万)" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 金额维度分析 */}
      <Card title="金额维度分析">
        <Row gutter={[16, 28]} className="mb-6">
          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">金额分布（直方图）</div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={amountDistributionDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="资产数量" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
        </div>
          </Col>

          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">金额排名（Top 10 资产）</div>
              <Table
                size="small"
                pagination={false}
                dataSource={topAssetsFinal}
                rowKey="id"
                columns={[
                  {
                    title: '排名',
                    dataIndex: 'rank',
                    width: 60,
                  },
                  {
                    title: '资产名称',
                    dataIndex: 'name',
                    ellipsis: true,
                  },
                  {
                    title: '类型',
                    dataIndex: 'type',
                    width: 100,
                    render: (value: string) => <Tag color="blue">{value}</Tag>,
                  },
                  {
                    title: '金额(万)',
                    dataIndex: 'amount',
                    width: 90,
                  },
                ]}
              />
            </div>
          </Col>

          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">金额累计（帕累托曲线）</div>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={paretoDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                  <Tooltip formatter={(value, name) => (name === '累计占比' ? `${value}%` : `${value} 万`)} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="amount" name="金额(万)" fill="#10b981" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cumulativeRatio"
                    name="累计占比"
                    stroke="#ef4444"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default Analytics